import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { AUTH_REDIRECT_KEY } from '@/components/auth/GoogleSignInButton';
import { FullPageState } from '@/components/ui/PageState';
import { isGerman } from '@/i18n/locale';

export default function AuthCallback() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);

  useEffect(() => {
    // Supabase auto-handles the OAuth code-for-session exchange via the
    // detectSessionInUrl client option. Once that lands, the auth store's
    // onAuthStateChange fires and `user` populates.
    if (loading) return;

    if (user) {
      // Pull and clear the stashed redirect target. If the user clicked
      // "Continue with Google" from /pricing, we'll send them back to
      // /pricing?plan=monthly to auto-resume checkout.
      const target = readAndClearRedirect();
      navigate(target ?? '/today', { replace: true });
      return;
    }

    // Edge case: auth completed but no user — likely a Google-side denial.
    // Send them back to login with a hint.
    const t = setTimeout(() => navigate('/login?error=oauth_failed', { replace: true }), 400);
    return () => clearTimeout(t);
  }, [user, loading, navigate]);

  return <FullPageState label={isGerman() ? 'Du wirst angemeldet…' : 'Signing you in…'} />;
}

// Reads the redirect target stashed by GoogleSignInButton, validates it,
// and clears it. Returns null if there's nothing valid to redirect to.
//
// The validation here mirrors the writer's check — defense in depth in
// case the writer's check is bypassed or the stored value was tampered.
function readAndClearRedirect(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(AUTH_REDIRECT_KEY);
    if (raw !== null) sessionStorage.removeItem(AUTH_REDIRECT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}
